import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth/AuthContext'
import { get, post } from 'aws-amplify/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'

type StackSummary = {
  StackName?: string
  StackStatus?: string
  StackStatusReason?: string
  CreationTime?: string
  LastUpdatedTime?: string
}

type SimulationStack = {
  name: string
  eventId: string
  environment: string
  status: string
  statusReason?: string
  createdAt?: string
  updatedAt?: string
  createdAtMs: number
  updatedAtMs: number
}

const environmentOptions = [
  { label: 'Dev', value: 'dev' },
  { label: 'UAT', value: 'uat' }
]

const environmentLabels: Record<string, string> = {
  dev: 'Dev',
  dev2: 'Dev 2',
  uat: 'UAT',
  prod: 'Prod',
  unknown: 'Unknown'
}

type StackAlert = {
  variant: 'info' | 'success' | 'error'
  message: string
} | null

export default function Simulations() {
  const { session, isAuthenticated } = useAuth()
  const { toast } = useToast()

  const [eventId, setEventId] = useState('afl-simulation-999999')
  const [environment, setEnvironment] = useState('dev')
  const [result, setResult] = useState('Ready')
  const [isProcessing, setIsProcessing] = useState(false)

  const [stacks, setStacks] = useState<SimulationStack[]>([])
  const [isLoadingStacks, setIsLoadingStacks] = useState(false)
  const [stacksError, setStacksError] = useState<string | null>(null)
  const [stackAlert, setStackAlert] = useState<StackAlert>(null)
  const [terminatingStacks, setTerminatingStacks] = useState<Set<string>>(new Set())

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadStacks = useCallback(async () => {
    if (!session?.tokens?.idToken) {
      return
    }

    try {
      setIsLoadingStacks(true)
      setStacksError(null)

      const request = get({
        apiName: 'dashboard',
        path: '/data/cfn',
        options: {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.tokens.idToken}`
          },
          withCredentials: true
        }
      })

      const { body } = await request.response
      const data = (await body.json()) as StackSummary[] | undefined

      const filtered = (Array.isArray(data) ? data : [])
        .filter((stack) => isSimulationStack(stack.StackName))
        .filter((stack) => (stack.StackStatus ?? '').toUpperCase() !== 'DELETE_COMPLETE')
        .map(normaliseStack)
        .sort((a, b) => b.updatedAtMs - a.updatedAtMs)

      setStacks(filtered)

      const activeNames = new Set(filtered.map((stack) => stack.name))
      setTerminatingStacks((prev) => {
        const updated = new Set(prev)
        Array.from(updated)
          .filter((name) => !activeNames.has(name))
          .forEach((name) => updated.delete(name))
        return updated
      })
    } catch (error) {
      console.error('Failed to load simulation stacks:', error)
      const message =
        error instanceof Error ? error.message : 'Unable to load CloudFormation stacks.'
      setStacksError(message)
    } finally {
      setIsLoadingStacks(false)
    }
  }, [isAuthenticated, session?.tokens?.idToken])

  useEffect(() => {
    if (!session || !isAuthenticated) {
      setStacks([])
      setStacksError(null)
      return
    }

    loadStacks()

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
    }
  }, [isAuthenticated, loadStacks, session])

  const scheduleRefresh = (delay = 6000) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }
    refreshTimerRef.current = setTimeout(() => {
      if (session && isAuthenticated) {
        loadStacks()
      }
    }, delay)
  }

  const callSimulationEndpoint = async (path: string, payload: Record<string, unknown>) => {
    if (!session?.tokens?.idToken) {
      throw new Error('You must be signed in to manage simulations.')
    }

    const request = post({
      apiName: 'dashboard',
      path,
      options: {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.tokens.idToken}`
        },
        withCredentials: true,
        body: payload
      }
    })

    const { body } = await request.response
    return body.json()
  }

  const setupSimulation = async () => {
    try {
      setIsProcessing(true)
      setResult('Provisioning simulation resources…')

      const response = await callSimulationEndpoint('/orchestration/setupSimulation', {
        eventId,
        environment
      })

      setResult(JSON.stringify(response, null, 2))
      toast({
        title: 'Simulation setup started',
        description: `Provisioning event ${eventId} in ${environment}`
      })
      await loadStacks()
      scheduleRefresh()
    } catch (error) {
      console.error('Error setting up simulation:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      setResult(`Failed to setup simulation: ${message}`)
      toast({
        title: 'Simulation setup failed',
        description: message,
        variant: 'destructive'
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const tearDownSimulation = async () => {
    try {
      setIsProcessing(true)
      setResult('Tearing down simulation resources…')

      const response = await callSimulationEndpoint('/orchestration/tearDownSimulation', {
        eventId,
        environment
      })

      setResult(JSON.stringify(response, null, 2))
      toast({
        title: 'Simulation teardown requested',
        description: `Teardown requested for ${eventId} in ${environment}`
      })
      await loadStacks()
      scheduleRefresh()
    } catch (error) {
      console.error('Error tearing down simulation:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      setResult(`Failed to tear down simulation: ${message}`)
      toast({
        title: 'Simulation teardown failed',
        description: message,
        variant: 'destructive'
      })
    } finally {
      setIsProcessing(false)
    }
  }

  const terminateStack = async (stack: SimulationStack) => {
    const confirmed = window.confirm(
      `Terminate CloudFormation stack "${stack.name}"?\n\nEnvironment: ${stack.environment}\nEvent: ${stack.eventId}\n\nThis will remove all resources created for this simulation.`
    )

    if (!confirmed) return

    setTerminatingStacks((prev) => new Set(prev).add(stack.name))
    setStackAlert({ variant: 'info', message: `Termination requested for ${stack.name}…` })

    try {
      await callSimulationEndpoint('/orchestration/tearDownSimulation', {
        eventId: stack.eventId,
        environment: stack.environment
      })
      setStackAlert({
        variant: 'success',
        message: `Teardown requested for ${stack.name}. Refreshing…`
      })
      toast({
        title: 'Termination requested',
        description: `${stack.name} is now tearing down`
      })
      await loadStacks()
      scheduleRefresh(8000)
    } catch (error) {
      console.error('Failed to delete stack:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      setStackAlert({
        variant: 'error',
        message: `Failed to delete ${stack.name}: ${message}`
      })
    } finally {
      setTerminatingStacks((prev) => {
        const next = new Set(prev)
        next.delete(stack.name)
        return next
      })
    }
  }

  const statusBadge = (stack: SimulationStack) => {
    const statusUpper = stack.status.toUpperCase()

    if (statusUpper.includes('FAILED') || statusUpper.includes('ROLLBACK')) {
      return <Badge className="bg-rose-500/15 text-rose-100 hover:bg-rose-500/20">{stack.status}</Badge>
    }

    if (statusUpper.startsWith('DELETE_')) {
      return <Badge className="bg-slate-700/60 text-slate-200 hover:bg-slate-700/80">{stack.status}</Badge>
    }

    if (statusUpper.includes('IN_PROGRESS')) {
      return (
        <Badge className="bg-amber-500/15 text-amber-100 hover:bg-amber-500/20">
          {stack.status}
        </Badge>
      )
    }

    return (
      <Badge className="bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/20">
        {stack.status}
      </Badge>
    )
  }

  if (!isAuthenticated) {
    return (
      <Card className="mx-auto max-w-3xl border-slate-800 bg-slate-900/70 shadow-card">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-semibold text-slate-100">
            Simulation Control
          </CardTitle>
          <CardDescription className="text-slate-400">
            Sign in to provision or retire simulation environments.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="space-y-10">
      <Card className="border-slate-800 bg-slate-900/70 shadow-card">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-2xl font-semibold text-slate-100">
              Simulation Orchestration
            </CardTitle>
            <CardDescription className="text-sm text-slate-400">
              Trigger automation that provisions or tears down event simulations.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            className="border border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
            onClick={() => loadStacks()}
            disabled={isLoadingStacks}
          >
            {isLoadingStacks ? 'Refreshing…' : 'Refresh Stacks'}
          </Button>
        </CardHeader>
        <CardContent className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-950/60 p-6">
            <div>
              <Label htmlFor="eventId" className="text-xs uppercase tracking-wide text-slate-400">
                Event ID
              </Label>
              <Input
                id="eventId"
                className="mt-2"
                type="text"
                placeholder="afl-simulation-999999"
                value={eventId}
                onChange={(event) => setEventId(event.target.value)}
                disabled={isProcessing}
              />
              <p className="mt-1 text-xs text-slate-500">
                Event IDs must include the <code className="rounded bg-slate-800 px-1 py-0.5 text-[10px]">-simulation-</code> suffix.
              </p>
            </div>

            <div>
              <Label
                htmlFor="environment"
                className="text-xs uppercase tracking-wide text-slate-400"
              >
                Environment
              </Label>
              <Select value={environment} onValueChange={setEnvironment}>
                <SelectTrigger id="environment" className="mt-2">
                  <SelectValue placeholder="Select environment" />
                </SelectTrigger>
                <SelectContent>
                  {environmentOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={setupSimulation} disabled={isProcessing}>
                Provision Simulation
              </Button>
              <Button
                variant="destructive"
                onClick={tearDownSimulation}
                disabled={isProcessing}
                className="bg-rose-500/80 text-rose-50 hover:bg-rose-500 disabled:bg-rose-500/40"
              >
                Teardown Simulation
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="result" className="text-xs uppercase tracking-wide text-slate-400">
                Result
              </Label>
              <textarea
                id="result"
                className="min-h-[9rem] w-full rounded-lg border border-slate-800 bg-slate-950 p-3 font-mono text-xs text-slate-200"
                value={result}
                readOnly
              />
            </div>
          </div>

          <div className="space-y-4 rounded-xl border border-slate-800 bg-slate-950/40 p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300">
              Working notes
            </h2>
            <ul className="list-disc space-y-2 pl-5 text-xs text-slate-400">
              <li>Provisioning copies baseline artefacts, deploys the event stack, and invalidates caches.</li>
              <li>Stacks appear below within a few seconds; use the refresh control to poll CloudFormation.</li>
              <li>Stack deletions are asynchronous – watch for <em>DELETE_IN_PROGRESS</em>.</li>
              <li>Re-run provisioning to rebuild a simulation after teardown completes.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-800 bg-slate-900/70 shadow-card">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-2xl font-semibold text-slate-100">
              Active Simulation Stacks
            </CardTitle>
            <CardDescription className="text-sm text-slate-400">
              CloudFormation stacks provisioning simulation resources across all environments.
            </CardDescription>
          </div>
          <span className="rounded-full bg-slate-800 px-4 py-1 text-sm font-semibold text-slate-300">
            {stacks.length} {stacks.length === 1 ? 'stack' : 'stacks'}
          </span>
        </CardHeader>
        <CardContent className="space-y-4">
          {stackAlert && (
            <div
              className={cn(
                'rounded-lg border px-4 py-3 text-sm',
                stackAlert.variant === 'success' && 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100',
                stackAlert.variant === 'error' && 'border-rose-500/40 bg-rose-500/10 text-rose-100',
                stackAlert.variant === 'info' && 'border-slate-700 bg-slate-800 text-slate-200'
              )}
            >
              {stackAlert.message}
            </div>
          )}

          {isLoadingStacks ? (
            <div className="flex justify-center py-12">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-accent" />
            </div>
          ) : stacksError ? (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-6 text-sm text-rose-100">
              {stacksError}
            </div>
          ) : stacks.length === 0 ? (
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-6 text-sm text-slate-400">
              No active simulation stacks. Provision a simulation above to see new stacks appear.
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-800">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-900/80 text-xs uppercase tracking-wide text-slate-400">
                    <TableRow>
                      <TableHead>Environment &amp; Event</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Created</TableHead>
                      <TableHead className="hidden lg:table-cell">Last Updated</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-slate-900/40">
                    {stacks.map((stack) => (
                      <TableRow
                        key={stack.name}
                        className={cn(
                          'transition-colors',
                          stack.status.toUpperCase().includes('FAILED') ||
                            stack.status.toUpperCase().includes('ROLLBACK')
                            ? 'bg-rose-500/10 hover:bg-rose-500/20'
                            : stack.status.toUpperCase().includes('IN_PROGRESS')
                              ? 'bg-amber-500/10 hover:bg-amber-500/20'
                              : stack.status.toUpperCase().startsWith('DELETE_')
                                ? 'bg-slate-900/40 hover:bg-slate-900/30'
                                : 'hover:bg-slate-800/40'
                        )}
                      >
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={cn(
                                  'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold',
                                  stack.environment === 'prod' && 'bg-rose-500/20 text-rose-100',
                                  stack.environment === 'uat' && 'bg-purple-500/20 text-purple-100',
                                  stack.environment === 'dev2' && 'bg-sky-500/20 text-sky-100',
                                  stack.environment === 'dev' && 'bg-blue-500/20 text-blue-100',
                                  stack.environment === 'unknown' && 'bg-slate-700/60 text-slate-200'
                                )}
                              >
                                {environmentLabels[stack.environment] ?? stack.environment}
                              </span>
                              <span className="font-mono text-xs text-slate-100 md:text-sm">
                                {stack.eventId}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">{stack.name}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {statusBadge(stack)}
                            {stack.statusReason && (
                              <p className="text-xs text-slate-500">{stack.statusReason}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden text-slate-300 lg:table-cell">
                          {stack.createdAt ? formatDate(stack.createdAt) : '—'}
                        </TableCell>
                        <TableCell className="hidden text-slate-300 lg:table-cell">
                          {stack.updatedAt ? formatDate(stack.updatedAt) : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="destructive"
                            className="bg-rose-500/80 text-rose-50 hover:bg-rose-500 disabled:cursor-not-allowed disabled:bg-rose-500/40 disabled:text-rose-100"
                            onClick={() => terminateStack(stack)}
                            disabled={
                              terminatingStacks.has(stack.name) ||
                              stack.status.toUpperCase().startsWith('DELETE_')
                            }
                          >
                            {terminatingStacks.has(stack.name) ? 'Terminating…' : 'Terminate'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

const isSimulationStack = (name?: string) => {
  if (!name) return false
  const lower = name.toLowerCase()
  if (lower === 'eventsimulation') return false
  return lower.includes('simulation')
}

const inferEnvironment = (name: string): string => {
  const lower = name.toLowerCase()
  if (lower.startsWith('prod')) return 'prod'
  if (lower.startsWith('uat')) return 'uat'
  if (lower.startsWith('dev2')) return 'dev2'
  if (lower.startsWith('dev')) return 'dev'
  return 'unknown'
}

const inferEventId = (name: string) => {
  const byStack = name.split('EventStack-')
  if (byStack.length > 1) {
    return byStack[1]
  }
  const match = name.match(/(afl[w]?)-simulation-[\w-]+/i)
  return match ? match[0] : name
}

const normaliseStack = (raw: StackSummary): SimulationStack => {
  const name = raw.StackName ?? 'unknown'
  const createdAt = raw.CreationTime
  const updatedAt = raw.LastUpdatedTime ?? raw.CreationTime

  return {
    name,
    status: raw.StackStatus ?? 'UNKNOWN',
    statusReason: raw.StackStatusReason,
    environment: inferEnvironment(name),
    eventId: inferEventId(name),
    createdAt,
    updatedAt,
    createdAtMs: createdAt ? Date.parse(createdAt) : 0,
    updatedAtMs: updatedAt ? Date.parse(updatedAt) : 0
  }
}

const formatDate = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  })}`
}
